import { formatDateTime } from "@/common/dateUtils";
import InsightCard from "@/components/chat/InsightCard.tsx";
import DeepInsightSearchBar from "@/components/deep-insight/DeepInsightSearchBar.tsx";
import { Button } from "@/components/ui/button.tsx";
import { ScrollArea } from "@/components/ui/scroll-area.tsx";
import { getDeepInsights } from "@/lib/serverComm.tsx";
import { DeepInsightSummary } from "@/types/deep-insights.ts";
import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";

const DeepInsights: React.FC = () => {
  const [isLoading, setIsLoading] = useState(false);
  const [insights, setInsights] = useState<DeepInsightSummary[] | null>(null);
  const [filteredInsights, setFilteredInsights] = useState<
    DeepInsightSummary[] | null
  >(null);
  const [lastUpdate, setLastUpdate] = useState<Date | null>(null);
  const [searchQuery, setSearchQuery] = useState("");

  useEffect(() => {
    if (isLoading || insights !== null) {
      return;
    }

    reload().then();
  }, [isLoading, insights]);

  // depending on `insights` and `searchQuery` --> set filtered insights
  useEffect(() => {
    // if nothing to filter --> exit
    if (insights === null) {
      return;
    }

    // if empty search --> reset to full-list
    if (searchQuery.length === 0) {
      setFilteredInsights(insights);
      return;
    }

    // otherwise we filter
    const query = searchQuery.toLowerCase();

    const filteredInsights = insights.filter((item) => {
      if (item.title === undefined || item.description === undefined) {
        return false; // some data is corrupted
      }

      const title = item.title.toLowerCase();
      const description = item.description.toLowerCase();
      return title.includes(query) || description.includes(query);
    });

    setFilteredInsights(filteredInsights);
  }, [insights, searchQuery]);

  const reload = async () => {
    setIsLoading(true);

    const response = await getDeepInsights();

    setIsLoading(false);
    setInsights(response);

    setLastUpdate(
      new Date(
        response.reduce((max, insight) => {
          const current = new Date(insight.date).getTime();
          return current > max ? current : max;
        }, 0),
      ),
    );
  };

  return (
    <ScrollArea className="h-full">
      <div className="p-8">
        <div className="flex items-center w-full gap-4">
          <div className="flex flex-col">
            <h1 className="text-sky-500 text-2xl font-bold">Deep Insights</h1>
          </div>
          <div className="flex-1"></div>
          <DeepInsightSearchBar
            className="mx-auto"
            onInput={setSearchQuery}
            isLoading={isLoading}
          />
          <Link to={`/strategic-initiatives`}>
            <Button className="bg-sky-500 rounded-full w-48 justify-central">
              <span className="flex-1">Strategic Initiatives</span>
            </Button>
          </Link>
        </div>
        {isLoading || filteredInsights === null ? (
          <p className="mt-8">Loading...</p>
        ) : (
          <>
            <p className="text-xs">
              Last updated:{" "}
              <strong>
                {lastUpdate === null ? "N/A" : formatDateTime(lastUpdate)}
              </strong>
            </p>
            <div className="mt-8 grid grid-cols-3 gap-6">
              {filteredInsights.map((insight) => (
                <InsightCard
                  key={insight.id}
                  id={insight.id}
                  chip={insight.chip}
                  category={insight.category}
                  title={insight.title}
                  description={insight.description}
                  date={insight.date}
                  isPinned={insight.isPinned}
                  isArchived={insight.isArchived}
                  reload={reload}
                />
              ))}
            </div>
          </>
        )}
      </div>
    </ScrollArea>
  );
};

export default DeepInsights;
